import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, DatePicker, Input, Select, Table, Tooltip } from "antd";
import { RefreshCw, Search, ShieldAlert } from "lucide-react";
import dayjs from "dayjs";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { isAdminUser } from "../../../utils/helpers";
import { listAuditEvents } from "../../../service/auditLogService";
import { SettingsCard, SettingsPage } from "../settingsUi";
import { actorLabel, describeAction, describeTarget, OUTCOMES, TARGET_TYPES } from "./auditLogModel";
import { ResultPill } from "./auditLogUi";
import AuditEventDrawer from "./AuditEventDrawer";

// Settings → Audit Logs: every change made in the console, refusals included.
// POST /api/v1/audit/list (authnull-service), administrators only. Filters and
// paging live in the URL: ?actor=&outcome=&targetType=&from=&to=&page=&pageSize=

const DESCRIPTION =
  "Every change made in the console is recorded here, including attempts that were refused. Entries cannot be edited or deleted.";

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const RANGE_PRESETS = [
  { label: "Last 24 hours", value: () => [dayjs().subtract(24, "hour"), dayjs()] },
  { label: "Last 7 days", value: () => [dayjs().subtract(7, "day"), dayjs()] },
  { label: "Last 30 days", value: () => [dayjs().subtract(30, "day"), dayjs()] },
  { label: "Last 90 days", value: () => [dayjs().subtract(90, "day"), dayjs()] },
];

const parseTime = (s) => {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d : null;
};

function NotAdmin() {
  return (
    <SettingsPage title="Audit Logs" description={DESCRIPTION}>
      <SettingsCard padding="1.25rem">
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", fontSize: "0.875rem", color: AN_COLORS.ink2 }}>
          <ShieldAlert size={18} color={AN_COLORS.muted} style={{ flexShrink: 0 }} />
          Only administrators can see audit logs.
        </div>
      </SettingsCard>
    </SettingsPage>
  );
}

export default function AuditLogsPage() {
  const admin = isAdminUser();
  const [params, setParams] = useSearchParams();

  // The default range (last 7 days) is fixed when the page opens, so it does
  // not slide on every refetch. It is not written to the URL until changed.
  const [defaultRange] = useState(() => [dayjs().subtract(7, "day"), dayjs()]);

  const actor = params.get("actor") || "";
  const outcome = params.get("outcome") || "";
  const targetType = params.get("targetType") || "";
  const from = parseTime(params.get("from")) || defaultRange[0];
  const to = parseTime(params.get("to")) || defaultRange[1];
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const pageSizeParam = parseInt(params.get("pageSize"), 10);
  const pageSize = PAGE_SIZES.includes(pageSizeParam) ? pageSizeParam : DEFAULT_PAGE_SIZE;
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  const [actorDraft, setActorDraft] = useState(actor);
  const [data, setData] = useState({ total: 0, events: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // { forbidden } | { message }
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);

  // Patch the URL; any filter change goes back to page 1.
  const update = useCallback(
    (patch, { keepPage = false } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === "" || v == null) next.delete(k);
            else next.set(k, String(v));
          });
          if (!keepPage) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Keep the box in step with the URL (back/forward), and commit typing after
  // a short pause.
  useEffect(() => setActorDraft(actor), [actor]);
  useEffect(() => {
    if (actorDraft.trim() === actor) return undefined;
    const t = setTimeout(() => update({ actor: actorDraft.trim() }), 400);
    return () => clearTimeout(t);
  }, [actorDraft, actor, update]);

  useEffect(() => {
    if (!admin) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAuditEvents({ actor, outcome, targetType, from: fromIso, to: toIso, page, pageSize })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        const apiMsg = err && err.response && err.response.data && err.response.data.error;
        setError(status === 403 ? { forbidden: true } : { message: apiMsg || "The audit log could not be loaded." });
        setData({ total: 0, events: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [admin, actor, outcome, targetType, fromIso, toIso, page, pageSize, reload]);

  const columns = useMemo(
    () => [
      {
        title: "Time",
        dataIndex: "at",
        key: "at",
        width: "9.5rem",
        render: (at) => {
          const d = dayjs(at);
          if (!d.isValid()) return at || "";
          return (
            <Tooltip title={d.format("D MMM YYYY, HH:mm:ss Z")}>
              <span style={{ whiteSpace: "nowrap", color: AN_COLORS.ink2 }}>{d.format("D MMM, HH:mm:ss")}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Who",
        key: "who",
        width: "18%",
        ellipsis: { showTitle: false },
        render: (_, e) => {
          const label = actorLabel(e);
          return (
            <Tooltip title={label} placement="topLeft">
              <span>{label}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        render: (_, e) => (
          <span style={{ color: AN_COLORS.ink, overflowWrap: "anywhere" }}>{describeAction(e)}</span>
        ),
      },
      {
        title: "Target",
        key: "target",
        width: "17%",
        ellipsis: { showTitle: false },
        render: (_, e) => {
          const t = describeTarget(e);
          if (!t.primary && !t.secondary) return <span style={{ color: AN_COLORS.muted }}>None</span>;
          return (
            <Tooltip title={e.targetId || t.primary} placement="topLeft">
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{t.primary}</span>
              {t.secondary && (
                <span style={{ display: "block", fontSize: "0.75rem", color: AN_COLORS.muted, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.secondary}
                </span>
              )}
            </Tooltip>
          );
        },
      },
      {
        title: "Result",
        dataIndex: "outcome",
        key: "outcome",
        width: "6.25rem",
        render: (o) => <ResultPill outcome={o} />,
      },
      {
        title: "Client IP",
        dataIndex: "clientIp",
        key: "clientIp",
        width: "8.5rem",
        ellipsis: true,
        render: (ip) => <span style={{ color: AN_COLORS.ink2 }}>{ip || ""}</span>,
      },
    ],
    [],
  );

  if (!admin || (error && error.forbidden)) return <NotAdmin />;

  const filtered = !!(actor || outcome || targetType || params.get("from") || params.get("to"));

  return (
    <SettingsPage
      title="Audit Logs"
      description={DESCRIPTION}
      actions={
        <Button icon={<RefreshCw size={14} />} onClick={() => setReload((n) => n + 1)} loading={loading}>
          Refresh
        </Button>
      }
    >
      {/* A grid, not a flex row: a global rule makes every Select width 100%, so each needs its own cell. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))",
          gap: "0.625rem",
          alignItems: "center",
        }}
      >
        <Input
          allowClear
          value={actorDraft}
          onChange={(ev) => setActorDraft(ev.target.value)}
          onPressEnter={() => update({ actor: actorDraft.trim() })}
          prefix={<Search size={14} color={AN_COLORS.muted} />}
          placeholder="Who (email)"
          aria-label="Filter by who"
        />
        <Select
          allowClear
          value={outcome || undefined}
          onChange={(v) => update({ outcome: v || "" })}
          placeholder="Any result"
          aria-label="Filter by result"
          options={Object.entries(OUTCOMES).map(([value, { label }]) => ({ value, label }))}
        />
        <Select
          allowClear
          value={targetType || undefined}
          onChange={(v) => update({ targetType: v || "" })}
          placeholder="Any target type"
          aria-label="Filter by target type"
          options={Object.entries(TARGET_TYPES).map(([value, label]) => ({ value, label }))}
        />
        <DatePicker.RangePicker
          showTime={{ format: "HH:mm" }}
          format="D MMM YYYY, HH:mm"
          allowClear={false}
          value={[from, to]}
          presets={RANGE_PRESETS}
          onChange={(range) => {
            if (!range || !range[0] || !range[1]) return;
            update({ from: range[0].toISOString(), to: range[1].toISOString() });
          }}
          aria-label="Time range"
          style={{ width: "100%", gridColumn: "span 2", minWidth: 0 }}
        />
        {filtered && (
          <Button
            type="link"
            onClick={() => {
              setActorDraft("");
              update({ actor: "", outcome: "", targetType: "", from: "", to: "" });
            }}
            style={{ paddingInline: "0.25rem" }}
          >
            Reset filters
          </Button>
        )}
      </div>

      {error && error.message && (
        <Alert
          type="error"
          showIcon
          message={error.message}
          action={
            <Button size="small" onClick={() => setReload((n) => n + 1)}>
              Try again
            </Button>
          }
        />
      )}

      <SettingsCard>
        <Table
          rowKey={(e) => e.eventId || `${e.at}-${e.route}`}
          size="middle"
          tableLayout="fixed"
          loading={loading}
          columns={columns}
          dataSource={data.events}
          style={{ fontFamily: AN_FONT }}
          locale={{
            emptyText: (
              <div style={{ padding: "2rem 0", color: AN_COLORS.muted, fontSize: "0.8125rem" }}>
                {filtered ? "No changes match these filters." : "No console changes recorded in the last 7 days."}
              </div>
            ),
          }}
          onRow={(e) => ({
            onClick: () => setSelected(e),
            onKeyDown: (ev) => {
              if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                setSelected(e);
              }
            },
            tabIndex: 0,
            style: { cursor: "pointer" },
            "aria-label": `${describeAction(e)}, details`,
          })}
          pagination={{
            current: page,
            pageSize,
            total: data.total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZES.map(String),
            showTotal: (total, [a, b]) => (total ? `${a}-${b} of ${total}` : ""),
            onChange: (p, size) => {
              if (size !== pageSize) update({ pageSize: size === DEFAULT_PAGE_SIZE ? "" : size });
              else update({ page: p === 1 ? "" : p }, { keepPage: true });
            },
            style: { padding: "0 1rem" },
          }}
        />
      </SettingsCard>

      <AuditEventDrawer event={selected} onClose={() => setSelected(null)} />
    </SettingsPage>
  );
}
